import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UnlinkIntentDialog } from "@/components/UnlinkIntentDialog";
import { Bank, ArrowsClockwise, Plus, LinkSimple, Warning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Bite 2: links a bank for the provenance ledger (transactions → bank APUs →
// reconciliation). Separate from PlaidConnect, which only mirrors balances.
export const BankLedgerConnect = ({ hid, onChange }) => {
  const [items, setItems] = useState([]);
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [unlinkItem, setUnlinkItem] = useState(null);

  const loadItems = useCallback(() => {
    if (!hid) return;
    api.get(`/plaid/bite2/items?household_id=${hid}`).then((r) => setItems(r.data)).catch(() => {});
  }, [hid]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const fetchLinkToken = useCallback(async () => {
    try {
      const r = await api.post(`/plaid/link/token/create?household_id=${hid}`);
      const token = r?.data?.link_token;
      if (!token) throw new Error("Missing Plaid link token");
      setLinkToken(token);
      sessionStorage.setItem("plaid_oauth_state", JSON.stringify({
        token,
        exchange: "ledger",
        hid,
        resume_path: window.location.pathname,
      }));
    } catch (e) {
      if (e?.response?.status === 503) setUnavailable(true);
    }
  }, [hid]);

  useEffect(() => { if (hid) fetchLinkToken(); }, [hid, fetchLinkToken]);

  const onSuccess = useCallback(async (public_token) => {
    setBusy(true);
    try {
      const r = await api.post(`/plaid/link/exchange?household_id=${hid}`, { public_token });
      const n = r.data?.sync?.new_bank_apus ?? 0;
      toast.success(`Linked ${r.data.institution} · ${n} transaction(s) ingested`);
      loadItems(); onChange?.(); fetchLinkToken();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link account");
    } finally { setBusy(false); }
  }, [hid, loadItems, onChange, fetchLinkToken]);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess });

  const syncTxns = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/plaid/transactions/sync?household_id=${hid}`);
      const added = (r.data?.results || []).reduce((s, x) => s + (x.new_bank_apus || 0), 0);
      const superseded = (r.data?.results || []).reduce((s, x) => s + (x.relink_superseded || 0), 0);
      toast.success(`Synced ${r.data.items_synced} item(s) · ${added} new transaction(s)`);
      if (superseded > 0) {
        toast.success(`${superseded} historical transactions reconciled from your previous connection`);
      }
      loadItems(); onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const disconnectOnly = async (item) => {
    try {
      await api.post(`/plaid/items/${item.id}/remove?household_id=${hid}`);
      toast.success(`Unlinked ${item.institution_name}`);
      loadItems();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlink");
      throw e;
    }
  };

  const onErased = async () => {
    toast.success("Connection data erased");
    loadItems();
    onChange?.();
  };

  const unlink = (item) => {
    setUnlinkItem(item);
  };

  if (unavailable) return null;

  const activeItems = items.filter((it) => it.status !== "removed");

  return (
    <div data-testid="bank-ledger-connect" className="bg-card border border-border rounded-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-sm bg-primary/10 grid place-items-center text-primary">
            <Bank size={18} weight="bold" />
          </span>
          <div>
            <p className="font-display text-base font-bold leading-tight">Bank ledger</p>
            <p className="text-xs text-muted-foreground">Settled transactions cross-check your statements</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeItems.length > 0 && (
            <Button data-testid="bank-ledger-sync-button" variant="outline" size="sm" onClick={syncTxns} disabled={busy} className="rounded-sm font-semibold">
              <ArrowsClockwise size={15} weight="bold" className={busy ? "mr-1.5 animate-spin" : "mr-1.5"} /> Sync transactions
            </Button>
          )}
          <Button data-testid="bank-ledger-connect-button" size="sm" onClick={() => open()} disabled={!ready || busy} className="rounded-sm font-semibold">
            <Plus size={15} weight="bold" className="mr-1" /> Connect a bank
          </Button>
        </div>
      </div>

      {activeItems.length > 0 && (
        <div className="mt-4 divide-y divide-border border-t border-border">
          {activeItems.map((it) => (
            <div key={it.id} data-testid={`bank-ledger-item-${it.id}`} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <LinkSimple size={15} className="text-success shrink-0" />
                <span className="font-semibold text-sm truncate">{it.institution_name}</span>
                {it.status === "needs_reauth" && (
                  <span className="text-xs text-warning flex items-center gap-1 shrink-0">
                    <Warning size={13} /> needs re-login
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">{it.status}</span>
                <button data-testid={`bank-ledger-unlink-${it.id}`} onClick={() => unlink(it)} className="text-muted-foreground hover:text-destructive shrink-0" title="Unlink">
                  <Trash size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <UnlinkIntentDialog
        open={Boolean(unlinkItem)}
        onOpenChange={(nextOpen) => { if (!nextOpen) setUnlinkItem(null); }}
        item={unlinkItem}
        hid={hid}
        allowErase
        onDisconnect={disconnectOnly}
        onErased={onErased}
      />
    </div>
  );
};
